import { useContext } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ProductCompleted, IProductCompletedCreate } from "../logic/api";
import { SessionContext } from "../utility/useSession";

// this is to list completed products for a booking
export const useProductsCompleted = (bookingId: number) => {
    const { current: session } = useContext(SessionContext);
    const userUuid = session?.site?.user?.uuid;
    const tenantId = session?.site?.tenant?.id;

    return useQuery<ProductCompleted[], Error>({
        queryKey: ["productsCompleted", userUuid, tenantId, session?.site?.home?.id, bookingId],
        queryFn: async () => {
            if (!session?.context) {
                throw new Error("Session context not available");
            }
            const response = await fetch(`${session.context.url}/productsCompleted/list/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...session.context.headers,
                },
                body: JSON.stringify({ bookingId }),
            });
            if (!response.ok) {
                throw new Error(`Failed to fetch products completed: ${response.statusText}`);
            }
            return response.json();
        },
        enabled: !!userUuid && !!tenantId && !!bookingId && !!session?.context,
    });
};

// this is to mark a product as completed
export const useCreateProductCompleted = (bookingId: number) => {
    const { current: session } = useContext(SessionContext);
    const userUuid = session?.site?.user?.uuid;
    const tenantId = session?.site?.tenant?.id;
    const queryClient = useQueryClient();

    return useMutation<ProductCompleted, Error, IProductCompletedCreate>({
        mutationFn: async (payload) => {
            if (!session?.context) {
                throw new Error("Session context not available");
            }
            const response = await fetch(`${session.context.url}/productsCompleted/create/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...session.context.headers,
                },
                body: JSON.stringify(payload),
            });
            if (!response.ok) {
                throw new Error(`Failed to create product completed: ${response.statusText}`);
            }
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["productsCompleted", userUuid, tenantId, session?.site?.home?.id, bookingId] });
        },
    });
};
